import { useEffect, useState } from 'react';
import { EventBus, useGameStore, useJwt } from '@campus/core';
import { Button, Icon } from '../ds/index.js';
import { jwtRole, FREE_DESK_PLACEMENT_ROLES } from '../../lib/jwtRole.js';

/**
 * PersonalDeskPrompt — invite des bureaux personnels (état : store
 * `personalDeskUi`, tenu par game/systems/PersonalDesks.ts) :
 * - sur un emplacement libre, sans bureau : « Prendre ce bureau » ;
 * - ailleurs dans un espace bureaux, sans bureau : « Choisis un emplacement
 *   libre » (et, pour OWNER/ADMIN, « Poser mon bureau ici » : pose libre) ;
 * - pendant la pose libre : « Poser ici » / « Annuler » ;
 * - sur un bureau : « Ton bureau » (Personnaliser, Libérer avec confirmation) ou « Bureau de X ».
 */
export function PersonalDeskPrompt() {
  const ui = useGameStore((s) => s.personalDeskUi);
  const jwt = useJwt();
  const [confirmRelease, setConfirmRelease] = useState(false);

  // La confirmation ne survit pas à un changement de bureau.
  useEffect(() => { setConfirmRelease(false); }, [ui.desk?.id]);

  const role = jwtRole(jwt) ?? '';
  const isMember = ['OWNER', 'ADMIN', 'MEMBER'].includes(role);
  const canPlaceFreely = FREE_DESK_PLACEMENT_ROLES.includes(role);
  const emit = (event: 'ui:personal-desk-place' | 'ui:personal-desk-claim' | 'ui:personal-desk-confirm' | 'ui:personal-desk-cancel' | 'ui:personal-desk-release' | 'ui:personal-desk-customize') => EventBus.emit(event, {});

  if (ui.customizing) return null; // DeskCustomizePanel prend le relais

  if (ui.placing) {
    return (
      <div className="pl-float-bottom">
        <div className="pl-pop" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 6px 6px 14px', fontSize: 13 }}>
          <Icon name="monitor" size={16} className="pl-menu-icon" />
          <span style={{ flex: 1, fontWeight: 500 }}>
            {ui.placing.valid ? 'Clique sur la carte ou sur « Poser ici »' : 'Choisis un emplacement libre (en vert)'}
          </span>
          <Button variant="ghost" size="sm" onClick={() => emit('ui:personal-desk-cancel')}>Annuler</Button>
          <Button variant="primary" size="sm" disabled={!ui.placing.valid} onClick={() => emit('ui:personal-desk-confirm')}>Poser ici</Button>
        </div>
      </div>
    );
  }

  if (ui.desk) {
    const { desk } = ui;
    return (
      <div className="pl-float-bottom">
        <div className="pl-pop" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: desk.isMine ? '6px 6px 6px 14px' : '10px 14px', fontSize: 13 }}>
          <Icon name="monitor" size={16} className="pl-menu-icon" />
          <span style={{ flex: 1, fontWeight: 500 }}>
            {!desk.isMine ? `Bureau de ${desk.ownerName}` : confirmRelease ? 'Ton aménagement sera effacé.' : 'Ton bureau'}
          </span>
          {desk.isMine && (confirmRelease ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setConfirmRelease(false)}>Garder</Button>
              <Button variant="danger" size="sm" onClick={() => { setConfirmRelease(false); emit('ui:personal-desk-release'); }}>Libérer</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" icon="help-circle" aria-label="Comment ça marche ?" title="Comment ça marche ?" onClick={() => EventBus.emit('ui:desk-tutorial-open', {})} />
              <Button variant="ghost" size="sm" onClick={() => setConfirmRelease(true)}>Libérer</Button>
              <Button variant="primary" size="sm" onClick={() => emit('ui:personal-desk-customize')}>Personnaliser</Button>
            </>
          ))}
        </div>
      </div>
    );
  }

  if (ui.area && isMember && !ui.myDesk) {
    const help = (
      <Button variant="ghost" size="sm" icon="help-circle" aria-label="Comment ça marche ?" title="Comment ça marche ?" onClick={() => EventBus.emit('ui:desk-tutorial-open', {})} />
    );
    if (ui.slot) {
      return (
        <div className="pl-float-bottom">
          <div className="pl-pop" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 6px 6px 14px', fontSize: 13 }}>
            <Icon name="monitor" size={16} className="pl-menu-icon" />
            <span style={{ flex: 1, fontWeight: 500 }}>Bureau libre</span>
            {help}
            <Button variant="primary" size="sm" onClick={() => emit('ui:personal-desk-claim')}>Prendre ce bureau</Button>
          </div>
        </div>
      );
    }
    return (
      <div className="pl-float-bottom">
        <div className="pl-pop" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 6px 6px 14px', fontSize: 13 }}>
          <Icon name="monitor" size={16} className="pl-menu-icon" />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 500 }}>{ui.area.name}</span>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {ui.areaHasFreeSlot ? ' · Va sur un emplacement libre pour le prendre' : ' · Aucun emplacement libre ici'}
            </span>
          </span>
          {help}
          {canPlaceFreely && (
            <Button variant={ui.areaHasFreeSlot ? 'ghost' : 'primary'} size="sm" onClick={() => emit('ui:personal-desk-place')}>Poser mon bureau ici</Button>
          )}
        </div>
      </div>
    );
  }
  return null;
}
